import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  InputGroup,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import { Eye, MoreHorizontal, Save, Send } from "lucide-react";
import { getToken } from "../../utilities/authHelper";
import "./InsightManagement.css";

const PAGE_SIZE = 20;

const EMPTY_ARTICLE = Object.freeze({
  id: null,
  title: "",
  slug: "",
  tags: [],
  tags_text: "",
  body_html: "",
  cover_image_url: "",
  status: "draft",
  created_at: null,
  updated_at: null,
  published_at: null,
});

const getApiUrl = (path) =>
  `${String(process.env.REACT_APP_API_URL || "").replace(/\/$/, "")}${path}`;

const getJsonHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getToken()}`,
});

const parseResponse = async (response, fallbackMessage) => {
  const envelope = await response.json().catch(() => null);

  if (!response.ok || envelope?.success === false) {
    throw new Error(
      envelope?.errorObject?.message || envelope?.message || fallbackMessage,
    );
  }

  return envelope?.results ?? {};
};

const getItems = (results, keys) => {
  if (Array.isArray(results)) return results;

  for (const key of keys) {
    if (Array.isArray(results?.[key])) return results[key];
  }

  return [];
};

const getEntity = (results, keys) => {
  if (!results || Array.isArray(results)) return null;

  for (const key of keys) {
    if (results[key] && typeof results[key] === "object") return results[key];
  }

  return results.id || results.insight_id ? results : null;
};

const getEntityId = (entity) => entity?.id || entity?.insight_id || null;

const parseTags = (value) =>
  Array.from(
    new Set(
      (Array.isArray(value) ? value : String(value || "").split(","))
        .map((tag) => String(tag || "").trim().toLowerCase())
        .filter(Boolean),
    ),
  );

const formatDateTime = (value) => {
  if (!value) return "—";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);

  return parsed.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getStatus = (insight) => {
  const status = String(insight?.status || insight?.insight_status || "").toLowerCase();
  if (["draft", "published", "archived"].includes(status)) return status;
  return insight?.published_at ? "published" : "draft";
};

const normalizeInsight = (insight = {}) => {
  const tags = parseTags(insight.tags);

  return {
    id: getEntityId(insight),
    title: insight.title || "",
    slug: insight.slug || "",
    tags,
    tags_text: tags.join(", "),
    body_html: insight.body_html || "",
    cover_image_url: insight.cover_image_url || "",
    status: getStatus(insight),
    created_at: insight.created_at || null,
    updated_at: insight.updated_at || null,
    published_at: insight.published_at || null,
    excerpt: insight.excerpt || "",
  };
};

const normalizeImage = (image = {}) => ({
  id: image.id || image.image_id || null,
  s3_url: image.s3_url || image.url || "",
  alt_text: image.alt_text || "",
  caption: image.caption || "",
  width: image.width || image.image_width || null,
  height: image.height || image.image_height || null,
  content_type: image.content_type || "",
  size: image.size || image.size_bytes || null,
  variants: image.variants || {},
  created_at: image.created_at || null,
});

const getInsightImageUrl = (insight = {}) => {
  const candidates = [
    insight.cover_image_url,
    insight.cover_image?.s3_url,
    insight.cover_image?.url,
    insight.image,
    insight.image_url,
  ];

  return (
    candidates.find((value) => typeof value === "string" && value.trim())?.trim() ||
    ""
  );
};

const fetchPublicInsights = async () => {
  const response = await fetch(getApiUrl("/insights/get_insights?limit=20&offset=0"), {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const results = await parseResponse(response, "Unable to load insights.");
  const items = getItems(results, ["insights", "insight_details", "items"]);

  // Keep the existing Home component compatible until its article-card work begins.
  return {
    insight_details: items.map((insight) => {
      const excerpt = insight.text || insight.excerpt || "";

      return {
        ...insight,
        // The article API exposes its card image as `cover_image_url`, while
        // older Home cards read `image`. Preserve both names so each client
        // renders the exact same cover without relying on a backend alias.
        image: getInsightImageUrl(insight),
        cover_image_url: getInsightImageUrl(insight),
        date: insight.date || insight.published_at || "",
        text: insight.text || (excerpt ? `${excerpt}…` : ""),
        url: insight.url || (insight.slug ? `https://emalpha.com/research/${encodeURIComponent(insight.slug)}` : ""),
      };
    }),
  };
};

// Home currently imports this helper. Admin uses the dedicated admin API below.
export const fetchInsights = fetchPublicInsights;

const fetchAdminInsights = async ({ status, offset }) => {
  const url = new URL(getApiUrl("/insights/get_insights_admin"));
  if (status !== "all") url.searchParams.set("status", status);
  url.searchParams.set("limit", String(PAGE_SIZE));
  url.searchParams.set("offset", String(offset));

  const response = await fetch(url.toString(), { headers: getJsonHeaders() });
  const results = await parseResponse(response, "Unable to load insights.");

  return {
    items: getItems(results, ["insights", "insight_details", "items"]).map(normalizeInsight),
    total: Number(results?.total ?? results?.total_count ?? results?.count ?? 0),
  };
};

const fetchAdminInsight = async (insightId) => {
  const url = new URL(getApiUrl("/insights/get_insight_admin"));
  url.searchParams.set("insight_id", insightId);

  const response = await fetch(url.toString(), { headers: getJsonHeaders() });
  const results = await parseResponse(response, "Unable to load insight.");
  const insight = getEntity(results, ["insight", "article", "insight_detail"]);

  if (!insight) throw new Error("The insight response did not include an article.");
  return normalizeInsight(insight);
};

const saveInsight = async ({ insightId, payload }) => {
  const url = new URL(getApiUrl("/insights/create_or_update_insight"));
  if (insightId) url.searchParams.set("insight_id", insightId);

  const response = await fetch(url.toString(), {
    method: "POST",
    headers: getJsonHeaders(),
    body: JSON.stringify(payload),
  });
  const results = await parseResponse(response, "Unable to save insight.");
  return getEntity(results, ["insight", "article", "insight_detail"]);
};

const publishInsight = async (insightId) => {
  const url = new URL(getApiUrl("/insights/publish_insight"));
  url.searchParams.set("insight_id", insightId);

  const response = await fetch(url.toString(), {
    method: "POST",
    headers: getJsonHeaders(),
  });
  return parseResponse(response, "Unable to publish insight.");
};

const archiveInsight = async (insightId) => {
  const url = new URL(getApiUrl("/insights/archive_insight"));
  url.searchParams.set("insight_id", insightId);

  const response = await fetch(url.toString(), {
    method: "POST",
    headers: getJsonHeaders(),
  });
  return parseResponse(response, "Unable to archive insight.");
};

const deleteInsight = async (insightId) => {
  const url = new URL(getApiUrl("/insights/delete_insight"));
  url.searchParams.set("insight_id", insightId);

  const response = await fetch(url.toString(), {
    method: "DELETE",
    headers: getJsonHeaders(),
  });
  return parseResponse(response, "Unable to delete insight.");
};

const fetchImages = async ({ offset }) => {
  const url = new URL(getApiUrl("/insights/get_images"));
  url.searchParams.set("limit", String(PAGE_SIZE));
  url.searchParams.set("offset", String(offset));

  const response = await fetch(url.toString(), { headers: getJsonHeaders() });
  const results = await parseResponse(response, "Unable to load the image library.");

  return {
    items: getItems(results, ["images", "image_details", "items"]).map(normalizeImage),
    total: Number(results?.total ?? results?.total_count ?? results?.count ?? 0),
  };
};

const uploadImage = async ({ file, altText, caption }) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("alt_text", altText);
  if (caption) formData.append("caption", caption);

  const response = await fetch(getApiUrl("/insights/upload_image"), {
    method: "POST",
    headers: { Authorization: `Bearer ${getToken()}` },
    body: formData,
  });
  const results = await parseResponse(response, "Unable to upload image.");
  return getEntity(results, ["image", "image_detail"]);
};

const updateImageMetadata = async ({ imageId, altText, caption }) => {
  const url = new URL(getApiUrl("/insights/update_image"));
  url.searchParams.set("image_id", imageId);

  const response = await fetch(url.toString(), {
    method: "POST",
    headers: getJsonHeaders(),
    body: JSON.stringify({ alt_text: altText, caption: caption || null }),
  });
  return parseResponse(response, "Unable to update image metadata.");
};

const escapeAttribute = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const unsafeUrlScheme = ["java", "script:"].join("");

const sanitizeHtmlForEditor = (html) => {
  if (typeof document === "undefined") return String(html || "");

  const template = document.createElement("template");
  template.innerHTML = String(html || "");
  template.content
    .querySelectorAll("script, iframe, object, embed, form, input, button")
    .forEach((node) => node.remove());

  template.content.querySelectorAll("*").forEach((node) => {
    Array.from(node.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const value = String(attribute.value || "").trim().toLowerCase();
      if (
        name.startsWith("on") ||
        ((name === "href" || name === "src") && value.startsWith(unsafeUrlScheme))
      ) {
        node.removeAttribute(attribute.name);
      }
    });
  });

  return template.innerHTML;
};

const buildPreviewDocument = ({ title, coverImageUrl, bodyHtml }) => `<!doctype html>
<html><head><meta charset="utf-8" />
<style>
  * { box-sizing: border-box; }
  body { margin: 0; color: #172033; font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; line-height: 1.7; background: #fff; }
  article { max-width: 760px; margin: 0 auto; padding: 28px 24px 48px; }
  h1 { font-size: clamp(1.9rem, 5vw, 2.75rem); letter-spacing: -0.035em; line-height: 1.08; margin: 0 0 26px; }
  h2 { margin-top: 2rem; line-height: 1.2; } h3 { margin-top: 1.5rem; }
  img { max-width: 100%; height: auto; border-radius: 12px; }
  a { color: #1c69b7; } blockquote { border-left: 3px solid #8bbbe8; color: #536176; margin: 1.5rem 0; padding: 0.4rem 1rem; }
  .cover { width: 100%; max-height: 360px; object-fit: cover; margin: 0 0 28px; }
  figcaption { color: #68748a; font-size: .9rem; text-align: center; }
</style></head><body><article>${
  coverImageUrl
    ? `<img class="cover" src="${escapeAttribute(coverImageUrl)}" alt="" />`
    : ""
}<h1>${String(title || "Untitled insight").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</h1>${
  bodyHtml || "<p>Start writing to preview the article.</p>"
}</article></body></html>`;

const StatusBadge = ({ status }) => (
  <Badge
    bg={status === "published" ? "success" : status === "archived" ? "secondary" : "warning"}
    text={status === "draft" ? "dark" : undefined}
    pill
  >
    {status}
  </Badge>
);

const RichHtmlEditor = forwardRef(({ value, onChange }, ref) => {
  const editorRef = useRef(null);
  const savedRangeRef = useRef(null);
  const [sourceMode, setSourceMode] = useState(false);

  const storeSelection = useCallback(() => {
    const selection = window.getSelection();
    if (!selection?.rangeCount || !editorRef.current?.contains(selection.anchorNode)) {
      return;
    }
    savedRangeRef.current = selection.getRangeAt(0).cloneRange();
  }, []);

  const restoreSelection = useCallback(() => {
    const range = savedRangeRef.current;
    if (!range) return;
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
  }, []);

  const emitCurrentHtml = useCallback(() => {
    onChange(editorRef.current?.innerHTML || "");
  }, [onChange]);

  const runCommand = useCallback(
    (command, commandValue = null) => {
      editorRef.current?.focus();
      restoreSelection();
      document.execCommand(command, false, commandValue);
      emitCurrentHtml();
      storeSelection();
    },
    [emitCurrentHtml, restoreSelection, storeSelection],
  );

  const insertImage = useCallback(
    (image) => {
      if (!image?.s3_url || !image?.alt_text) return;

      editorRef.current?.focus();
      restoreSelection();
      const caption = image.caption
        ? `<p><em>${escapeAttribute(image.caption)}</em></p>`
        : "";
      const imageHtml = `<p><img src="${escapeAttribute(image.s3_url)}" alt="${escapeAttribute(image.alt_text)}" /></p>${caption}<p><br></p>`;
      document.execCommand("insertHTML", false, imageHtml);
      emitCurrentHtml();
      storeSelection();
    },
    [emitCurrentHtml, restoreSelection, storeSelection],
  );

  useImperativeHandle(ref, () => ({ insertImage }), [insertImage]);

  useEffect(() => {
    if (!sourceMode && editorRef.current) {
      const safeHtml = sanitizeHtmlForEditor(value);
      if (editorRef.current.innerHTML !== safeHtml) editorRef.current.innerHTML = safeHtml;
    }
  }, [sourceMode, value]);

  const setEditorMode = (nextSourceMode) => {
    if (nextSourceMode === sourceMode) return;
    if (sourceMode) onChange(sanitizeHtmlForEditor(value));
    setSourceMode(nextSourceMode);
  };

  const createLink = () => {
    const href = window.prompt("Link URL");
    if (href?.trim()) runCommand("createLink", href.trim());
  };

  const handleDrop = (event) => {
    event.preventDefault();
    try {
      const image = JSON.parse(event.dataTransfer.getData("application/x-emalpha-image"));
      insertImage(image);
    } catch (error) {
      // Ignore drops that did not originate from the image library.
    }
  };

  return (
    <div className="insight-html-editor">
      <div className="insight-editor-toolbar" role="toolbar" aria-label="Editor controls">
        <Button size="sm" variant="light" type="button" title="Bold" onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand("bold")}>
          <strong>B</strong>
        </Button>
        <Button size="sm" variant="light" type="button" title="Italic" onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand("italic")}>
          <em>I</em>
        </Button>
        <Button size="sm" variant="light" type="button" title="Underline" onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand("underline")}>
          <u>U</u>
        </Button>
        <span className="insight-editor-divider" />
        <Button size="sm" variant="light" type="button" title="Heading" onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand("formatBlock", "h2")}>
          H2
        </Button>
        <Button size="sm" variant="light" type="button" title="Bulleted list" onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand("insertUnorderedList")}>
          • List
        </Button>
        <Button size="sm" variant="light" type="button" title="Numbered list" onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand("insertOrderedList")}>
          1. List
        </Button>
        <Button size="sm" variant="light" type="button" title="Quote" onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand("formatBlock", "blockquote")}>
          Quote
        </Button>
        <Button size="sm" variant="light" type="button" title="Add link" onMouseDown={(event) => event.preventDefault()} onClick={createLink}>
          Link
        </Button>
        <span className="insight-editor-toolbar-spacer" />
        <div className="insight-editor-mode-toggle" role="group" aria-label="Editor mode">
          <Button size="sm" variant={sourceMode ? "light" : "primary"} type="button" aria-pressed={!sourceMode} onClick={() => setEditorMode(false)}>
            Visual
          </Button>
          <Button size="sm" variant={sourceMode ? "primary" : "light"} type="button" aria-pressed={sourceMode} onClick={() => setEditorMode(true)}>
            HTML
          </Button>
        </div>
      </div>

      {sourceMode ? (
        <Form.Control
          as="textarea"
          rows={15}
          className="insight-editor-source"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          spellCheck={false}
          aria-label="Insight HTML source"
        />
      ) : (
        <div
          ref={editorRef}
          className="insight-editor-surface"
          contentEditable
          role="textbox"
          aria-multiline="true"
          aria-label="Insight body"
          data-placeholder="Write the insight, or drag an uploaded image here…"
          onInput={emitCurrentHtml}
          onBlur={storeSelection}
          onKeyUp={storeSelection}
          onMouseUp={storeSelection}
          onDrop={handleDrop}
          suppressContentEditableWarning
        />
      )}
      <div className="insight-editor-help">Drag an image from the media library into the article, or use Insert from the selected image.</div>
    </div>
  );
});

RichHtmlEditor.displayName = "RichHtmlEditor";

const ImageLibrary = ({
  isMinimized,
  onToggleMinimized,
  images,
  imagesLoading,
  selectedImage,
  onSelectImage,
  onUseAsCover,
  onInsertImage,
  onUploadImage,
  onUpdateImage,
  hasNextPage,
  hasPreviousPage,
  onNextPage,
  onPreviousPage,
}) => {
  const [file, setFile] = useState(null);
  const [altText, setAltText] = useState("");
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [metadataAlt, setMetadataAlt] = useState("");
  const [metadataCaption, setMetadataCaption] = useState("");
  const [savingMetadata, setSavingMetadata] = useState(false);
  const [uploadError, setUploadError] = useState("");

  useEffect(() => {
    setMetadataAlt(selectedImage?.alt_text || "");
    setMetadataCaption(selectedImage?.caption || "");
  }, [selectedImage]);

  const handleUpload = async (event) => {
    event.preventDefault();
    setUploadError("");

    if (!file) return setUploadError("Choose an image to upload.");
    if (!altText.trim()) return setUploadError("Alt text is required for every image.");
    if (file.size > 10 * 1024 * 1024) return setUploadError("Images must be 10 MB or smaller.");

    setUploading(true);
    try {
      await onUploadImage({ file, altText: altText.trim(), caption: caption.trim() });
      setFile(null);
      setAltText("");
      setCaption("");
      const input = document.getElementById("insight-image-upload-input");
      if (input) input.value = "";
    } catch (error) {
      setUploadError(error.message || "Unable to upload image.");
    } finally {
      setUploading(false);
    }
  };

  const handleSaveMetadata = async () => {
    if (!selectedImage?.id || !metadataAlt.trim()) return;
    setSavingMetadata(true);
    try {
      await onUpdateImage({
        imageId: selectedImage.id,
        altText: metadataAlt.trim(),
        caption: metadataCaption.trim(),
      });
    } catch (error) {
      // The parent surfaces the API error in the editorial workspace alert.
    } finally {
      setSavingMetadata(false);
    }
  };

  return (
    <aside className={`insight-media-library ${isMinimized ? "is-minimized" : ""}`} aria-label="Insight media library">
      <div className={`d-flex align-items-start justify-content-between gap-2 ${isMinimized ? "" : "mb-3"}`}>
        {!isMinimized ? (
          <div>
            <div className="insight-section-kicker">Media library</div>
            <h5 className="mb-0">Article images</h5>
          </div>
        ) : null}
        {!isMinimized ? <Badge bg="light" text="dark" pill>{images.length}</Badge> : null}
        <Button
          variant="outline-secondary"
          size="sm"
          className="insight-media-library-toggle"
          type="button"
          aria-label={isMinimized ? "Expand media library" : "Minimize media library"}
          title={isMinimized ? "Expand media library" : "Minimize media library"}
          onClick={onToggleMinimized}
        >
          {isMinimized ? "‹" : "›"}
        </Button>
      </div>

      {!isMinimized ? <>
      <Form onSubmit={handleUpload} className="insight-upload-form">
        <Form.Group className="mb-2">
          <Form.Label className="insight-field-label">Upload image</Form.Label>
          <Form.Control
            id="insight-image-upload-input"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(event) => setFile(event.target.files?.[0] || null)}
          />
        </Form.Group>
        <Form.Control className="mb-2" size="sm" value={altText} onChange={(event) => setAltText(event.target.value)} placeholder="Alt text (required)" />
        <Form.Control className="mb-2" size="sm" value={caption} onChange={(event) => setCaption(event.target.value)} placeholder="Caption (optional)" />
        {uploadError ? <div className="small text-danger mb-2">{uploadError}</div> : null}
        <Button type="submit" size="sm" className="w-100" disabled={uploading}>
          {uploading ? <><Spinner animation="border" size="sm" className="me-2" />Uploading</> : "Upload to library"}
        </Button>
      </Form>

      <div className="insight-media-grid mt-3">
        {imagesLoading ? (
          <div className="py-4 text-center"><Spinner animation="border" size="sm" /></div>
        ) : images.length ? (
          images.map((image) => (
            <button
              key={image.id || image.s3_url}
              type="button"
              draggable
              className={`insight-media-thumb ${selectedImage?.id === image.id ? "is-selected" : ""}`}
              title="Click to select, then drag into the editor"
              onClick={() => onSelectImage(image)}
              onDragStart={(event) => {
                event.dataTransfer.effectAllowed = "copy";
                event.dataTransfer.setData("application/x-emalpha-image", JSON.stringify(image));
              }}
            >
              <img src={image.s3_url} alt={image.alt_text} />
            </button>
          ))
        ) : (
          <div className="small text-muted text-center py-3">No uploaded images yet.</div>
        )}
      </div>

      {(hasPreviousPage || hasNextPage) ? (
        <div className="d-flex gap-2 mt-3">
          <Button variant="outline-secondary" size="sm" className="flex-grow-1" disabled={!hasPreviousPage} onClick={onPreviousPage}>Previous</Button>
          <Button variant="outline-secondary" size="sm" className="flex-grow-1" disabled={!hasNextPage} onClick={onNextPage}>Next</Button>
        </div>
      ) : null}

      {selectedImage ? (
        <div className="insight-selected-image mt-3">
          <div className="small fw-semibold text-truncate mb-2">Selected image</div>
          <img src={selectedImage.s3_url} alt={selectedImage.alt_text} />
          <div className="small text-muted mt-2">{selectedImage.width && selectedImage.height ? `${selectedImage.width} × ${selectedImage.height}` : "Image metadata"}</div>
          <Form.Control className="mt-2" size="sm" value={metadataAlt} onChange={(event) => setMetadataAlt(event.target.value)} placeholder="Alt text" />
          <Form.Control className="mt-2" size="sm" value={metadataCaption} onChange={(event) => setMetadataCaption(event.target.value)} placeholder="Caption" />
          <Button variant="outline-secondary" size="sm" className="w-100 mt-2" disabled={savingMetadata || !metadataAlt.trim()} onClick={handleSaveMetadata}>
            {savingMetadata ? "Saving…" : "Save image details"}
          </Button>
          <div className="d-flex gap-2 mt-2">
            <Button variant="outline-primary" size="sm" className="flex-grow-1" onClick={() => onUseAsCover(selectedImage)}>Set cover</Button>
            <Button size="sm" className="flex-grow-1" onClick={() => onInsertImage(selectedImage)}>Insert</Button>
          </div>
        </div>
      ) : null}
      </> : null}
    </aside>
  );
};

const InsightManagement = () => {
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [insights, setInsights] = useState([]);
  const [insightsTotal, setInsightsTotal] = useState(0);
  const [insightsOffset, setInsightsOffset] = useState(0);
  const [loadingInsights, setLoadingInsights] = useState(true);
  const [images, setImages] = useState([]);
  const [imagesTotal, setImagesTotal] = useState(0);
  const [imagesOffset, setImagesOffset] = useState(0);
  const [loadingImages, setLoadingImages] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [article, setArticle] = useState(EMPTY_ARTICLE);
  const [loadingArticle, setLoadingArticle] = useState(false);
  const [busyAction, setBusyAction] = useState("");
  const [notice, setNotice] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isMediaLibraryMinimized, setIsMediaLibraryMinimized] = useState(false);
  const editorRef = useRef(null);

  const loadInsights = useCallback(async () => {
    setLoadingInsights(true);
    try {
      const result = await fetchAdminInsights({ status: statusFilter, offset: insightsOffset });
      setInsights(result.items);
      setInsightsTotal(result.total);
    } catch (error) {
      setNotice({ type: "danger", message: error.message || "Unable to load insights." });
      setInsights([]);
    } finally {
      setLoadingInsights(false);
    }
  }, [insightsOffset, statusFilter]);

  const loadImages = useCallback(async () => {
    setLoadingImages(true);
    try {
      const result = await fetchImages({ offset: imagesOffset });
      setImages(result.items);
      setImagesTotal(result.total);
    } catch (error) {
      setNotice({ type: "danger", message: error.message || "Unable to load the image library." });
      setImages([]);
    } finally {
      setLoadingImages(false);
    }
  }, [imagesOffset]);

  useEffect(() => { void loadInsights(); }, [loadInsights]);
  useEffect(() => { void loadImages(); }, [loadImages]);

  const visibleInsights = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return insights;
    return insights.filter((insight) =>
      [insight.title, insight.slug, insight.tags.join(" ")]
        .join(" ")
        .toLowerCase()
        .includes(query),
    );
  }, [insights, search]);

  const updateArticle = (changes) => setArticle((current) => ({ ...current, ...changes }));

  const selectArticle = async (insightId) => {
    if (!insightId) return;
    setLoadingArticle(true);
    setNotice(null);
    try {
      setArticle(await fetchAdminInsight(insightId));
    } catch (error) {
      setNotice({ type: "danger", message: error.message || "Unable to open insight." });
    } finally {
      setLoadingArticle(false);
    }
  };

  const validateArticle = ({ publishing = false } = {}) => {
    if (!article.title.trim()) return "A title is required.";
    if (!String(article.body_html || "").replace(/<[^>]*>/g, "").trim()) return "The article body is required.";
    if (publishing && parseTags(article.tags_text).length === 0) return "Add at least one tag before publishing.";
    return "";
  };

  const persistArticle = async () => {
    const validationError = validateArticle();
    if (validationError) throw new Error(validationError);

    const saved = await saveInsight({
      insightId: article.id,
      payload: {
        title: article.title.trim(),
        slug: article.slug.trim() || undefined,
        tags: parseTags(article.tags_text),
        body_html: article.body_html,
        cover_image_url: article.cover_image_url.trim() || null,
      },
    });

    const savedId = getEntityId(saved) || article.id;
    if (!savedId) throw new Error("The draft saved but no insight ID was returned. Refresh the list before publishing.");
    const refreshed = await fetchAdminInsight(savedId);
    setArticle(refreshed);
    return refreshed;
  };

  const handleSaveDraft = async () => {
    setBusyAction("save");
    setNotice(null);
    try {
      await persistArticle();
      await loadInsights();
      setNotice({ type: "success", message: "Draft saved." });
    } catch (error) {
      setNotice({ type: "danger", message: error.message || "Unable to save draft." });
    } finally {
      setBusyAction("");
    }
  };

  const handlePublish = async () => {
    const validationError = validateArticle({ publishing: true });
    if (validationError) return setNotice({ type: "danger", message: validationError });

    setBusyAction("publish");
    setNotice(null);
    try {
      const savedArticle = await persistArticle();
      await publishInsight(savedArticle.id);
      setArticle(await fetchAdminInsight(savedArticle.id));
      await loadInsights();
      setNotice({ type: "success", message: "Insight published." });
    } catch (error) {
      setNotice({ type: "danger", message: error.message || "Unable to publish insight." });
    } finally {
      setBusyAction("");
    }
  };

  const handleArchive = async () => {
    if (!article.id) return;
    if (!window.confirm(`Archive “${article.title || "this insight"}”?`)) return;

    setBusyAction("archive");
    setNotice(null);
    try {
      await archiveInsight(article.id);
      setArticle((current) => ({ ...current, status: "archived" }));
      await loadInsights();
      setNotice({ type: "success", message: "Insight archived." });
    } catch (error) {
      setNotice({ type: "danger", message: error.message || "Unable to archive insight." });
    } finally {
      setBusyAction("");
    }
  };

  const handleDelete = async () => {
    if (!article.id) return;
    if (!window.confirm(`Delete “${article.title || "this insight"}”? This cannot be undone.`)) return;

    setBusyAction("delete");
    setNotice(null);
    try {
      await deleteInsight(article.id);
      setArticle({ ...EMPTY_ARTICLE });
      await loadInsights();
      setNotice({ type: "success", message: "Insight deleted." });
    } catch (error) {
      setNotice({ type: "danger", message: error.message || "Unable to delete insight." });
    } finally {
      setBusyAction("");
    }
  };

  const handleUploadImage = async (payload) => {
    const savedImage = normalizeImage(await uploadImage(payload) || {});
    setImagesOffset(0);
    await loadImages();
    if (savedImage.s3_url) setSelectedImage(savedImage);
    setNotice({ type: "success", message: "Image uploaded to the media library." });
  };

  const handleUpdateImage = async ({ imageId, altText, caption }) => {
    setNotice(null);
    try {
      await updateImageMetadata({ imageId, altText, caption });
      await loadImages();
      setSelectedImage((current) => current?.id === imageId ? { ...current, alt_text: altText, caption } : current);
      setNotice({ type: "success", message: "Image details updated." });
    } catch (error) {
      setNotice({ type: "danger", message: error.message || "Unable to update image details." });
      throw error;
    }
  };

  const noPreviousInsights = insightsOffset === 0;
  const hasNextInsights = insights.length === PAGE_SIZE && (insightsTotal === 0 || insightsOffset + PAGE_SIZE < insightsTotal);
  const noPreviousImages = imagesOffset === 0;
  const hasNextImages = images.length === PAGE_SIZE && (imagesTotal === 0 || imagesOffset + PAGE_SIZE < imagesTotal);

  return (
    <div className="insight-management-shell">
      <div className="insight-management-header">
        <div>
          <div className="insight-section-kicker">Editorial workspace</div>
          <h3 className="mb-1">Insights</h3>
          <p className="text-muted mb-0">Write, stage, publish, and manage the images used in research insights.</p>
        </div>
        <Button onClick={() => { setArticle({ ...EMPTY_ARTICLE }); setNotice(null); }}>
          + New insight
        </Button>
      </div>

      {notice ? <Alert variant={notice.type} dismissible onClose={() => setNotice(null)} className="mt-3 mb-0">{notice.message}</Alert> : null}

      <div className={`insight-management-layout mt-3 ${isMediaLibraryMinimized ? "is-media-library-minimized" : ""}`}>
        <section className="insight-list-panel">
          <div className="insight-list-panel-head">
            <Form.Select
              aria-label="Filter insights by status"
              size="sm"
              value={statusFilter}
              onChange={(event) => { setStatusFilter(event.target.value); setInsightsOffset(0); }}
            >
              <option value="all">All insights</option>
              <option value="draft">Drafts</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </Form.Select>
            <Form.Control size="sm" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search" aria-label="Search insights" />
          </div>
          <div className="insight-list-scroll">
            {loadingInsights ? (
              <div className="text-center py-5"><Spinner animation="border" size="sm" /></div>
            ) : visibleInsights.length ? visibleInsights.map((insight) => (
              <button
                type="button"
                key={insight.id || insight.slug}
                className={`insight-list-item ${article.id === insight.id ? "is-active" : ""}`}
                onClick={() => selectArticle(insight.id)}
              >
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <span className="insight-list-title">{insight.title || "Untitled insight"}</span>
                  <StatusBadge status={insight.status} />
                </div>
                <div className="insight-list-meta">{insight.slug ? `/${insight.slug}` : "No slug yet"}</div>
                <div className="insight-list-meta">{formatDateTime(insight.updated_at || insight.published_at || insight.created_at)}</div>
              </button>
            )) : (
              <div className="text-center text-muted small py-5">No insights match this view.</div>
            )}
          </div>
          {(insightsTotal > PAGE_SIZE || hasNextInsights) ? (
            <div className="d-flex gap-2 p-2 border-top">
              <Button size="sm" variant="outline-secondary" className="flex-grow-1" disabled={noPreviousInsights} onClick={() => setInsightsOffset((offset) => Math.max(0, offset - PAGE_SIZE))}>Previous</Button>
              <Button size="sm" variant="outline-secondary" className="flex-grow-1" disabled={!hasNextInsights} onClick={() => setInsightsOffset((offset) => offset + PAGE_SIZE)}>Next</Button>
            </div>
          ) : null}
        </section>

        <section className="insight-editor-panel">
          {loadingArticle ? (
            <div className="d-flex align-items-center justify-content-center h-100"><Spinner animation="border" /></div>
          ) : (
            <>
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <div className="d-flex align-items-center gap-2">
                  <StatusBadge status={article.status} />
                  {article.id ? <span className="small text-muted">Updated {formatDateTime(article.updated_at || article.created_at)}</span> : <span className="small text-muted">New draft</span>}
                </div>
                <div className="insight-editor-actions">
                  <div className="insight-editor-action-group">
                    <Button variant="outline-secondary" size="sm" className="insight-action-button" onClick={() => setShowPreview(true)}>
                      <Eye size={14} aria-hidden="true" />
                      Preview
                    </Button>
                    <Button variant="outline-primary" size="sm" className="insight-action-button" disabled={Boolean(busyAction)} onClick={handleSaveDraft}>
                      <Save size={14} aria-hidden="true" />
                      {busyAction === "save" ? "Saving…" : "Save draft"}
                    </Button>
                    <Button size="sm" className="insight-action-button insight-action-publish" disabled={Boolean(busyAction) || article.status === "archived"} onClick={handlePublish}>
                      <Send size={14} aria-hidden="true" />
                      {busyAction === "publish" ? "Publishing…" : article.status === "published" ? "Update & publish" : "Publish"}
                    </Button>
                  </div>
                  {article.id ? (
                    <Dropdown align="end" className="insight-editor-action-menu">
                      <Dropdown.Toggle variant="outline-secondary" size="sm" className="insight-action-menu-toggle" disabled={Boolean(busyAction)} aria-label="More insight actions" title="More insight actions">
                        <MoreHorizontal size={18} aria-hidden="true" />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {article.status !== "archived" ? (
                          <Dropdown.Item onClick={handleArchive}>
                            Archive insight
                          </Dropdown.Item>
                        ) : null}
                        {article.status !== "archived" ? <Dropdown.Divider /> : null}
                        <Dropdown.Item className="text-danger" onClick={handleDelete}>
                          Delete insight
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  ) : null}
                </div>
              </div>

              <Form.Group className="mb-3">
                <Form.Label className="insight-field-label">Title <span className="text-danger">*</span></Form.Label>
                <Form.Control value={article.title} onChange={(event) => updateArticle({ title: event.target.value })} placeholder="A concise, market-relevant headline" />
              </Form.Group>
              <Row className="g-3 mb-3">
                <Col md={7}>
                  <Form.Label className="insight-field-label">Slug <span className="text-muted fw-normal">(generated from title if left blank)</span></Form.Label>
                  <InputGroup>
                    <InputGroup.Text>/insights/</InputGroup.Text>
                    <Form.Control value={article.slug} onChange={(event) => updateArticle({ slug: event.target.value })} placeholder="dollar-strengthens-after-data" />
                  </InputGroup>
                </Col>
                <Col md={5}>
                  <Form.Label className="insight-field-label">Tags <span className="text-danger">*</span></Form.Label>
                  <Form.Control value={article.tags_text} onChange={(event) => updateArticle({ tags_text: event.target.value })} placeholder="fx, macro" />
                </Col>
              </Row>

              <Card className="insight-cover-card mb-3">
                <Card.Body className="p-3">
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <div>
                      <div className="insight-field-label mb-1">Cover image</div>
                      <div className="small text-muted">Select an uploaded image from the media library, or add a direct image URL.</div>
                    </div>
                    {article.cover_image_url ? <Button variant="link" size="sm" className="text-danger p-0" onClick={() => updateArticle({ cover_image_url: "" })}>Remove</Button> : null}
                  </div>
                  <Form.Control className="mt-2" size="sm" value={article.cover_image_url} onChange={(event) => updateArticle({ cover_image_url: event.target.value })} placeholder="https://…" />
                  {article.cover_image_url ? <img className="insight-cover-preview mt-2" src={article.cover_image_url} alt="Selected article cover" /> : null}
                </Card.Body>
              </Card>

              <Form.Label className="insight-field-label">Article body <span className="text-danger">*</span></Form.Label>
              <RichHtmlEditor ref={editorRef} value={article.body_html} onChange={(body_html) => updateArticle({ body_html })} />

            </>
          )}
        </section>

        <ImageLibrary
          isMinimized={isMediaLibraryMinimized}
          onToggleMinimized={() => setIsMediaLibraryMinimized((current) => !current)}
          images={images}
          imagesLoading={loadingImages}
          selectedImage={selectedImage}
          onSelectImage={setSelectedImage}
          onUseAsCover={(image) => updateArticle({ cover_image_url: image.s3_url })}
          onInsertImage={(image) => editorRef.current?.insertImage(image)}
          onUploadImage={handleUploadImage}
          onUpdateImage={handleUpdateImage}
          hasPreviousPage={!noPreviousImages}
          hasNextPage={hasNextImages}
          onPreviousPage={() => setImagesOffset((offset) => Math.max(0, offset - PAGE_SIZE))}
          onNextPage={() => setImagesOffset((offset) => offset + PAGE_SIZE)}
        />
      </div>

      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="xl" centered>
        <Modal.Header closeButton><Modal.Title>Article preview</Modal.Title></Modal.Header>
        <Modal.Body className="p-0 bg-light">
          <iframe
            title="Insight preview"
            className="insight-preview-frame"
            sandbox=""
            srcDoc={buildPreviewDocument({ title: article.title, coverImageUrl: article.cover_image_url, bodyHtml: article.body_html })}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default InsightManagement;
